import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { paths, REPORT_VIEW_QUERY } from '@/app/router/paths';
import { Button, Card, MonoLabel, Skeleton } from '@/design-system';
import { toPublicTenant } from '@/entities/publicReport';
import { formatReportAddress, pdfProblemOf, type ReportShareChanges } from '@/entities/report';
import { errorMessage, problemKey, useSessionStore } from '@/shared/api';
import { ErrorState } from '@/shared/ui';

import { copyText } from './browser';
import { previewCopy } from './copy';
import { AdminHeader } from './components/AdminHeader';
import { AdminSidebar, type LinkSetting } from './components/AdminSidebar';
import { PublicReportContent } from './components/PublicReportContent';
import { PublicReportLayout } from './components/PublicReportLayout';
import { useClientPagePreview } from './useClientPagePreview';
import { usePublicReportPreview } from './usePublicReportPreview';

/**
 * Het tabblad Klantpagina van een keuring (`/keuringen/:orderId/klantpagina`,
 * binnen de inlog): dezelfde pagina als de koper ziet, met het browserchroom en
 * de rechterkolom met linkstatistieken en toegang. Hoe vaak hij bekeken is en
 * de knoppen staan in de kop van de keuring.
 *
 * `:orderId` is de uuid van de keuring. Rapport, link en instellingen komen van
 * de API; de pagina zelf van `GET /reports/{report}/preview`: wat de koper met
 * zijn link ziet, zonder dat het als bezoek telt en zonder toegangscode.
 *
 * Alles gaat over de versie die de koper heeft. Staat er al een nieuwere versie
 * klaar die nog niet verstuurd is, dan zegt de kop dat.
 */
export function PublicReportPreviewPage() {
    const { orderId = '' } = useParams<{ orderId: string }>();
    const navigate = useNavigate();
    const session = useSessionStore((state) => state.session);
    const preview = useClientPagePreview(orderId);
    const page = usePublicReportPreview(preview.released?.reportUuid ?? null);

    const [notice, setNotice] = useState<{ tone: 'status' | 'alert'; text: string } | null>(null);
    const [copied, setCopied] = useState(false);

    const report = preview.newest;
    const released = preview.released;

    if (preview.report.isError) {
        return (
            <div className="p-[22px]">
                <ErrorState
                    error={preview.report.error}
                    title={previewCopy.errorTitle}
                    onRetry={() => void preview.report.refetch()}
                />
            </div>
        );
    }

    if (preview.report.data === undefined) {
        return (
            <div className="p-[22px]">
                <Skeleton height={420} radius={12} label={previewCopy.loading} />
            </div>
        );
    }

    if (report === null || released === null) {
        return (
            <div className="p-[22px]">
                <Card padding="lg" className="max-w-[640px]">
                    <MonoLabel size={10} className="mb-2 block">
                        {previewCopy.screenTitle}
                    </MonoLabel>
                    {/* De kop van de keuring draagt de h1 (het adres). */}
                    <h2 className="m-0 mb-2 font-[family-name:var(--font-display)] text-[17px] leading-[1.3] font-semibold text-[var(--text-primary)]">
                        {previewCopy.noReportTitle}
                    </h2>
                    <p className="m-0 text-[14px] leading-[1.5] text-[var(--text-secondary)]">
                        {previewCopy.noReportBody}
                    </p>
                    <div className="mt-4">
                        <Button
                            variant="primary"
                            size="md"
                            onClick={() => navigate(paths.report(orderId))}
                        >
                            {previewCopy.toReport}
                        </Button>
                    </div>
                </Card>
            </div>
        );
    }

    const statistics = preview.statistics.data ?? null;
    const address = page.report?.address ?? formatReportAddress(report.address);
    const companyName = page.report?.tenant.name ?? session?.tenant?.name ?? '';
    const phone = page.report?.tenant.phone ?? null;
    const busy =
        preview.updateShare.isPending || preview.createShare.isPending || preview.resend.isPending;

    const settingError = preview.updateShare.isError
        ? problemKey(preview.updateShare.error) === 'share-revoked'
            ? previewCopy.shareRevoked
            : previewCopy.settingFailed
        : preview.createShare.isError
          ? errorMessage(preview.createShare.error)
          : null;

    const toggle = (setting: LinkSetting, next: boolean) => {
        const changes: ReportShareChanges =
            setting === 'costs'
                ? { showCosts: next }
                : setting === 'forward'
                  ? { allowForward: next }
                  : next
                    ? { requireAccessCode: true }
                    : { removeAccessCode: true };

        preview.updateShare.mutate(changes);
    };

    const copyLink = async () => {
        if (!preview.url) {
            return;
        }

        const done = await copyText(preview.url);

        setCopied(done);
        setNotice(done ? null : { tone: 'alert', text: previewCopy.copyFailed });
    };

    const downloadPdf = () => {
        setNotice({ tone: 'status', text: previewCopy.pdfBusy });
        preview.pdf.mutate(undefined, {
            onSuccess: () => setNotice(null),
            onError: (error) =>
                setNotice({
                    tone: 'alert',
                    text:
                        pdfProblemOf(error) === 'setup'
                            ? previewCopy.pdfNotConfigured
                            : previewCopy.pdfFailed,
                }),
        });
    };

    const resend = () => {
        setNotice(null);
        preview.resend.mutate(undefined, {
            onSuccess: ({ delivery, newLink }) =>
                setNotice({
                    tone: 'status',
                    text: [
                        delivery.failed > 0
                            ? previewCopy.resendPartial(delivery.sent, delivery.failed)
                            : previewCopy.resendDone(delivery.sent),
                        newLink
                            ? previewCopy.resendNewLink
                            : preview.share?.accessCodeRequired
                              ? previewCopy.resendCodeNote
                              : null,
                    ]
                        .filter(Boolean)
                        .join(' '),
                }),
            onError: (error) => setNotice({ tone: 'alert', text: errorMessage(error) }),
        });
    };

    const header = (
        <AdminHeader
            eyebrow={previewCopy.eyebrow(statistics?.openedCount ?? 0)}
            note={
                released.reportUuid === report.uuid
                    ? null
                    : previewCopy.newerVersion(released.version, report.version)
            }
            notice={notice}
            actions={
                <>
                    {/* Dezelfde pagina zonder chroom en zonder deze kolom: over het hele scherm. */}
                    <Button
                        variant="onDark"
                        size="compact"
                        onClick={() =>
                            void navigate(paths.reportView(orderId, REPORT_VIEW_QUERY.value))
                        }
                    >
                        {previewCopy.openFull}
                    </Button>
                    <Button
                        variant="onDark"
                        size="compact"
                        disabled={!preview.url}
                        onClick={() => void copyLink()}
                    >
                        {copied ? previewCopy.copied : previewCopy.copyLink}
                    </Button>
                    {report.can.send ? (
                        <Button variant="onDark" size="compact" disabled={busy} onClick={resend}>
                            {previewCopy.resend}
                        </Button>
                    ) : null}
                    <Button
                        variant="primary"
                        size="compact"
                        onClick={() => navigate(paths.report(orderId))}
                    >
                        {previewCopy.editReport}
                    </Button>
                </>
            }
        />
    );

    const sidebar = (
        <AdminSidebar
            statistics={statistics}
            share={preview.share}
            canManage={report.can.share}
            busy={busy}
            error={settingError}
            issuedCode={preview.issuedCode}
            issuedCodeSent={preview.issuedCodeSent}
            onDismissCode={preview.dismissCode}
            onToggle={toggle}
            onNewCode={() => preview.updateShare.mutate({ requireAccessCode: true })}
            onCreateLink={() => preview.createShare.mutate()}
            companyName={companyName}
            address={address}
            contactLine={phone ? previewCopy.phoneContact(companyName, phone) : null}
        />
    );

    return (
        <PublicReportLayout
            tenant={
                page.tenant ?? fallbackTenant(companyName, session?.tenant?.accentColor ?? null)
            }
            language={page.language}
            admin={{ header, sidebar, chromeUrl: preview.url }}
        >
            <PublicReportContent
                state={page}
                headingLevel={2}
                onOpenDocument={() => {
                    if (!preview.pdf.isPending) {
                        downloadPdf();
                    }
                }}
                previewNote={previewCopy.previewNote}
                pinned={false}
            />
        </PublicReportLayout>
    );
}

/** Huisstijl uit de sessie, zolang de pagina zelf nog niet binnen is. */
function fallbackTenant(name: string, accentColor: string | null) {
    return toPublicTenant({
        name,
        logo_url: null,
        accent_color: accentColor,
        phone: null,
        opening_hours: null,
        kvk: null,
        memberships: [],
        languages: ['nl'],
    });
}
